import React, { CSSProperties, ReactNode, useEffect, useRef, useState } from 'react';
import { gql, useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { AlignLeft, ArrowLeft, ChevronRight, Construction, Download, Eye, Layers, Loader2, PenLine, Plus, Table2, TableProperties, Trash2, Upload } from 'lucide-react';
import DialogBase from '../../../base/DialogBase/DialogBase';
import { useDownloadAttachment, useLoadHttpClientEmpty } from '../../../App.hooks';
import KeyScheduleMapping from './KeyScheduleMapping';
import NomenclatureStandard from './NomenclatureStandard';
import ClassificationStandard from './ClassificationStandard';
import ParameterMappingStandard from './ParameterMappingStandard';
import TextStyleStandard from './TextStyleStandard';
import MustIncludeViewsStandard from './MustIncludeViewsStandard';

/* ------------------------------------------------------------------ */
/*  GraphQL                                                            */
/* ------------------------------------------------------------------ */

const GET_PROJECT_STANDARDS = gql`
    query getProjectStandards($id: String) {
        Project(id: $id) {
            dynamicData { id slug name description type data setData }
        }
    }
`;

const GET_CLIENT_STANDARDS = gql`
    query getClientStandards($libraryId: String) {
        Library(id: $libraryId) {
            directContents(first: 10, where: { name: { eq: "Client Standards" } }) {
                nodes {
                    id
                    name
                    applicationName
                    currentVersionId
                    types { id name description settings }
                }
            }
        }
    }
`;

const SAVE_CLIENT_STANDARDS = gql`
    mutation saveClientStandards($libraryId: String, $version: ContentVersionInput, $overwrite: Boolean!) {
        Library(id: $libraryId) {
            uploadContent(version: $version, tagIds: [], overwrite: $overwrite) {
                id
                currentVersionId
            }
        }
    }
`;

const SET_STANDARD_DYNAMIC_DATA = gql`
    mutation setStandardDynamicData($projectId: String, $data: DynamicDataInput) {
        Project(id: $projectId) { setDynamicData(data: $data) }
    }
`;

const DELETE_STANDARD_DYNAMIC_DATA = gql`
    mutation deleteStandardDynamicData($projectId: String, $ids: [String]) {
        Project(id: $projectId) { deleteDynamicData(ids: $ids) }
    }
`;

const CLIENT_STANDARDS_NAME = 'Client Standards';
const CLIENT_STANDARDS_APPLICATION = 'CMS';
const CONTENT_DATA_MAPPING_TYPE = 'ContentDataMapping';
const STANDARD_TYPE = 'Standard';
const CONTENT_DATA_MAPPING_ID = 'Content Data Mapping';

const getContentDataMappingSlug = (name: string) => {
    const slug = name.trim().toLocaleLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    return !slug || slug === 'content-data-mapping' ? 'content-mapping' : `content-mapping-${slug}`;
};

type ProjectStandardEntry = {
    id?: string;
    slug?: string;
    name?: string;
    description?: string;
    type?: string;
    data?: string;
    setData?: string;
};

type ClientStandardType = {
    id?: string;
    name?: string;
    description?: string;
    settings?: string;
};

type ContentDataMappingEnvelope = {
    kind: 'ContentDataMapping';
    slug?: string;
    data: unknown;
};

const getContentDataMappingEnvelope = (type: ClientStandardType): ContentDataMappingEnvelope | undefined => {
    if (typeof type.settings !== 'string') return undefined;
    try {
        const parsed = JSON.parse(type.settings);
        return parsed?.kind === 'ContentDataMapping' && Object.prototype.hasOwnProperty.call(parsed, 'data')
            ? parsed as ContentDataMappingEnvelope
            : undefined;
    } catch {
        return undefined;
    }
};

const PreviewBadge = () => (
    <span
        className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full"
        style={{ background: 'rgba(255,255,255,0.25)', letterSpacing: '0.04em' }}
    >
        Preview
    </span>
);

/* ------------------------------------------------------------------ */
/*  Standard definitions                                               */
/* ------------------------------------------------------------------ */

type StandardDefinition = {
    /** Must match the DynamicData.type value stored on the project */
    id: string;
    title: string;
    description: string;
    icon: React.ElementType;
    iconColor: string;
    iconBg: string;
    /** editor = has a full editor component; viewer = placeholder until implemented */
    mode: 'editor' | 'viewer';
    /** Controls availability outside the global-admin preview. */
    releaseStatus: 'released' | 'comingSoon';
};

const STANDARD_DEFINITIONS: StandardDefinition[] = [
    {
        id: CONTENT_DATA_MAPPING_ID,
        title: 'Content Data Mapping',
        description: 'Associate data values with library content, organised by Revit category',
        icon: TableProperties,
        iconColor: 'var(--accent)',
        iconBg: 'var(--accent-soft)',
        mode: 'editor',
        releaseStatus: 'comingSoon',
    },
    {
        id: 'Nomenclature',
        title: 'Nomenclature',
        description: 'Define naming conventions and standards for library content',
        icon: AlignLeft,
        iconColor: 'var(--warning)',
        iconBg: 'var(--warning-soft)',
        mode: 'editor',
        releaseStatus: 'released',
    },
    {
        id: 'Classification',
        title: 'Classification',
        description: 'Manage content classification systems and hierarchies',
        icon: Layers,
        iconColor: 'var(--info)',
        iconBg: 'var(--info-soft)',
        mode: 'editor',
        releaseStatus: 'released',
    },
    {
        id: 'Parameter Mapping',
        title: 'Parameter Mapping',
        description: 'Generic lookup table mapping parameter values between systems',
        icon: Table2,
        iconColor: 'var(--accent)',
        iconBg: 'var(--accent-soft)',
        mode: 'editor',
        releaseStatus: 'comingSoon',
    },
    {
        id: 'Text Style',
        title: 'Text Style',
        description: 'Define allowed Revit text style property values and a nomenclature pattern',
        icon: PenLine,
        iconColor: 'var(--success, #16a34a)',
        iconBg: 'var(--success-soft, #dcfce7)',
        mode: 'editor',
        releaseStatus: 'released',
    },
    {
        id: 'Must Include Views',
        title: 'Must Include Views',
        description: 'Specify the Revit views that every model must contain',
        icon: Eye,
        iconColor: 'var(--info)',
        iconBg: 'var(--info-soft)',
        mode: 'editor',
        releaseStatus: 'released',
    },
];

/* ------------------------------------------------------------------ */
/*  Standards tab component                                            */
/* ------------------------------------------------------------------ */

type StandardsProps = {
    projectId: string;
    libraryId?: string;
    clientName?: string;
    clientDefaultLibraryId?: string;
    canEdit?: boolean;
    canUsePreviewFeatures?: boolean;
    style?: CSSProperties | undefined;
    children?: ReactNode | undefined;
};

const Standards = ({
    projectId,
    libraryId,
    clientName,
    clientDefaultLibraryId,
    canEdit = false,
    canUsePreviewFeatures = false,
    style,
}: StandardsProps) => {
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [addMenuOpen, setAddMenuOpen] = useState(false);
    const [saveDialogOpen, setSaveDialogOpen] = useState(false);
    const [importDialogOpen, setImportDialogOpen] = useState(false);
    const [workflowError, setWorkflowError] = useState('');
    const [workflowStatus, setWorkflowStatus] = useState('');
    const [isSavingClientStandards, setIsSavingClientStandards] = useState(false);
    const [isImportingClientStandards, setIsImportingClientStandards] = useState(false);
    const [selectedImportNames, setSelectedImportNames] = useState<Set<string>>(new Set());
    const [addMappingDialogOpen, setAddMappingDialogOpen] = useState(false);
    const [newMappingName, setNewMappingName] = useState('');
    const [mappingNameError, setMappingNameError] = useState('');
    const addBtnRef = useRef<HTMLButtonElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const download = useDownloadAttachment();
    const httpClient = useLoadHttpClientEmpty();

    const { data, loading, refetch } = useQuery(GET_PROJECT_STANDARDS, {
        variables: { id: projectId },
        skip: !projectId,
        fetchPolicy: 'network-only',
    });

    const [setDynamicData, { loading: adding }] = useMutation(SET_STANDARD_DYNAMIC_DATA);
    const [deleteDynamicData] = useMutation(DELETE_STANDARD_DYNAMIC_DATA);
    const [saveClientStandards] = useMutation(SAVE_CLIENT_STANDARDS);
    const [loadClientStandards, clientStandardsResult] = useLazyQuery(GET_CLIENT_STANDARDS, {
        fetchPolicy: 'network-only',
    });

    // Close dropdown on outside click
    useEffect(() => {
        if (!addMenuOpen) return;
        const handler = (e: MouseEvent) => {
            if (
                !addBtnRef.current?.contains(e.target as Node) &&
                !menuRef.current?.contains(e.target as Node)
            ) {
                setAddMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [addMenuOpen]);

    const isContentDataMapping = (entry: ProjectStandardEntry) =>
        (entry?.type === STANDARD_TYPE && (
            entry?.slug?.startsWith('content-mapping')
            || entry?.description === 'Maps Revit Key Schedule keys to library content by category'
            || entry?.description === 'Maps data values to library content by category'
        ))
        || entry?.type === CONTENT_DATA_MAPPING_TYPE
        || entry?.type === 'KeyScheduleMapping'
        || (entry?.type === 'Standards' && (
            entry?.name === 'Key Schedule Mapping'
            || entry?.description === 'Maps Revit Key Schedule keys to library content by category'
            || entry?.description === 'Maps data values to library content by category'
        ));
    const getContentDataMappingName = (entry: ProjectStandardEntry) =>
        entry.type === 'KeyScheduleMapping' || entry.name === 'Key Schedule Mapping'
            ? CONTENT_DATA_MAPPING_ID
            : entry.name || CONTENT_DATA_MAPPING_ID;

    // Most standards use type='Standards', distinguished by name. Content data mappings
    // have their own repeatable type so each project can store several named mappings.
    const projectStandardEntries = ((data?.Project?.dynamicData ?? []) as ProjectStandardEntry[])
        .filter(d => d?.type === 'Standards' || d?.type === STANDARD_TYPE || d?.type === 'KeyScheduleMapping' || d?.type === CONTENT_DATA_MAPPING_TYPE);
    const contentDataMappings = projectStandardEntries.filter(isContentDataMapping);
    const storedIds = new Set<string>();
    const dynamicDataIdByName = new Map<string, string>();
    const dynamicDataIdBySlug = new Map<string, string>();
    for (const d of projectStandardEntries) {
        if (d?.type === 'Standards' && d?.name && !isContentDataMapping(d)) {
            storedIds.add(d.name);
            if (d.id) dynamicDataIdByName.set(d.name, d.id);
        }
        if (isContentDataMapping(d) && d.id) dynamicDataIdByName.set(getContentDataMappingName(d), d.id);
        if (isContentDataMapping(d) && d.id && d.slug) dynamicDataIdBySlug.set(d.slug, d.id);
    }
    const activeStandards = STANDARD_DEFINITIONS.filter(s => s.id !== CONTENT_DATA_MAPPING_ID && storedIds.has(s.id));
    const canAccessStandard = (standard: StandardDefinition) =>
        standard.releaseStatus === 'released' || canUsePreviewFeatures;
    const standardsToAdd = STANDARD_DEFINITIONS.filter(s => s.id === CONTENT_DATA_MAPPING_ID || !storedIds.has(s.id));
    // Any type='Standards' entries whose name isn't in STANDARD_DEFINITIONS
    const knownIds = new Set(STANDARD_DEFINITIONS.map(s => s.id));
    const unknownStoredNames: string[] = ((data?.Project?.dynamicData ?? []) as any[])
        .filter(d => d?.type === 'Standards' && d?.name && !isContentDataMapping(d) && !knownIds.has(d.name))
        .map(d => d.name as string);

    const clientStandardNodes = clientStandardsResult.data?.Library?.directContents?.nodes ?? [];
    const clientStandardContent = clientStandardNodes.find((content: any) => content?.applicationName === CLIENT_STANDARDS_APPLICATION)
        ?? clientStandardNodes[0];
    const clientStandardTypes = (clientStandardContent?.types ?? [])
        .filter((type: ClientStandardType) => !!type?.name && typeof type?.settings === 'string') as ClientStandardType[];
    const isClientContentDataMapping = (type: ClientStandardType) =>
        getContentDataMappingEnvelope(type) !== undefined
        || type.name === 'Key Schedule Mapping'
        || type.name === CONTENT_DATA_MAPPING_ID
        || type.description === 'Maps Revit Key Schedule keys to library content by category'
        || type.description === 'Maps data values to library content by category';

    const openSaveDialog = () => {
        setWorkflowError('');
        setWorkflowStatus('');
        setSaveDialogOpen(true);
    };

    const openImportDialog = async () => {
        if (!clientDefaultLibraryId) return;
        setWorkflowError('');
        setWorkflowStatus('');
        setSelectedImportNames(new Set());
        setImportDialogOpen(true);
        try {
            const result = await loadClientStandards({ variables: { libraryId: clientDefaultLibraryId } });
            const nodes = result.data?.Library?.directContents?.nodes ?? [];
            const content = nodes.find((item: any) => item?.applicationName === CLIENT_STANDARDS_APPLICATION) ?? nodes[0];
            const missingNames = (content?.types ?? [])
                .filter((type: ClientStandardType) => type?.name)
                .filter((type: ClientStandardType) => {
                    const slug = getContentDataMappingEnvelope(type)?.slug;
                    return !dynamicDataIdByName.has(type.name as string)
                        && !(slug && dynamicDataIdBySlug.has(slug));
                })
                .map((type: ClientStandardType) => type.name as string);
            setSelectedImportNames(new Set(missingNames));
        } catch (error) {
            setWorkflowError(error instanceof Error ? error.message : 'Unable to load the client standards.');
        }
    };

    const handleSaveToClient = async () => {
        if (!clientDefaultLibraryId) {
            setWorkflowError('This client does not have a Default Library.');
            return;
        }
        setIsSavingClientStandards(true);
        setWorkflowError('');
        try {
            const refreshed = await refetch();
            const entries = ((refreshed.data?.Project?.dynamicData ?? []) as ProjectStandardEntry[])
                .filter(entry => (
                    entry?.type === 'Standards'
                    || entry?.type === STANDARD_TYPE
                    || entry?.type === 'KeyScheduleMapping'
                    || entry?.type === CONTENT_DATA_MAPPING_TYPE
                ) && !!entry.name && !!entry.data);
            if (entries.length === 0) throw new Error('This project does not have any saved standard payloads.');

            const results = await Promise.all(entries.map(async entry => {
                const name = isContentDataMapping(entry) ? getContentDataMappingName(entry) : entry.name as string;
                try {
                    const raw = await download(entry.data as string);
                    const parsed = JSON.parse(typeof raw === 'string' ? raw : JSON.stringify(raw));
                    return {
                        type: {
                            name,
                            description: entry.description,
                            settings: JSON.stringify(isContentDataMapping(entry)
                                ? { kind: 'ContentDataMapping', slug: entry.slug, data: parsed }
                                : parsed),
                        },
                    };
                } catch {
                    return { missingName: name };
                }
            }));
            const types = results.flatMap(result => result.type ? [result.type] : []);
            const missingNames = results.flatMap(result => result.missingName ? [result.missingName] : []);
            if (types.length === 0) {
                throw new Error(`No valid saved payloads could be downloaded${missingNames.length ? `: ${missingNames.join(', ')}` : '.'}`);
            }

            await saveClientStandards({
                variables: {
                    libraryId: clientDefaultLibraryId,
                    overwrite: true,
                    version: {
                        name: CLIENT_STANDARDS_NAME,
                        category: 'Standards',
                        applicationName: CLIENT_STANDARDS_APPLICATION,
                        description: `Standards for ${clientName || 'client'}`,
                        types,
                    },
                },
            });
            setSaveDialogOpen(false);
            setWorkflowStatus(
                `Saved ${types.length} standard${types.length === 1 ? '' : 's'} as a new client version.`
                + (missingNames.length ? ` Skipped unavailable: ${missingNames.join(', ')}.` : ''),
            );
        } catch (error) {
            setWorkflowError(error instanceof Error ? error.message : 'Unable to save the client standards.');
        } finally {
            setIsSavingClientStandards(false);
        }
    };

    const toggleImportName = (name: string) => {
        setSelectedImportNames(previous => {
            const next = new Set(previous);
            if (next.has(name)) next.delete(name);
            else next.add(name);
            return next;
        });
    };

    const handleImportFromClient = async () => {
        const selectedTypes = clientStandardTypes.filter(type => type.name && selectedImportNames.has(type.name));
        if (selectedTypes.length === 0) {
            setWorkflowError('Select at least one client standard to add.');
            return;
        }
        setIsImportingClientStandards(true);
        setWorkflowError('');
        try {
            const missingTypes = selectedTypes.filter(type => {
                const slug = getContentDataMappingEnvelope(type)?.slug;
                return type.name
                    && !dynamicDataIdByName.has(type.name)
                    && !(slug && dynamicDataIdBySlug.has(slug));
            });
            for (const type of missingTypes) {
                const slug = getContentDataMappingEnvelope(type)?.slug;
                await setDynamicData({
                    variables: {
                        projectId,
                        data: {
                            slug: isClientContentDataMapping(type) ? slug : undefined,
                            name: type.name,
                            type: isClientContentDataMapping(type) ? STANDARD_TYPE : 'Standards',
                            description: type.description,
                        },
                    },
                });
            }

            const refreshed = await refetch();
            const refreshedEntries = (refreshed.data?.Project?.dynamicData ?? []) as ProjectStandardEntry[];
            for (const type of selectedTypes) {
                if (!type.name || typeof type.settings !== 'string') continue;
                const envelope = getContentDataMappingEnvelope(type);
                const slug = envelope?.slug;
                const settings = JSON.stringify(envelope ? envelope.data : JSON.parse(type.settings));
                const entry = refreshedEntries.find(item =>
                    (item?.type === 'Standards' && item?.name === type.name)
                    || (isClientContentDataMapping(type)
                        && item?.type === STANDARD_TYPE
                        && (slug ? item?.slug === slug : item?.name === type.name)
                    )
                    || (type.name === 'Key Schedule Mapping' && item?.type === 'KeyScheduleMapping'));
                if (!entry?.setData) throw new Error(`No upload URL was returned for ${type.name}.`);
                await httpClient.put(entry.setData, settings, {
                    headers: {
                        'x-ms-blob-type': 'BlockBlob',
                        'Content-Type': 'application/json',
                        'Cache-Control': 'no-cache, no-store, must-revalidate',
                    },
                });
            }
            await refetch();
            setImportDialogOpen(false);
            setWorkflowStatus(`Added ${selectedTypes.length} client standard${selectedTypes.length === 1 ? '' : 's'} to the project.`);
        } catch (error) {
            setWorkflowError(error instanceof Error ? error.message : 'Unable to add the client standards to the project.');
        } finally {
            setIsImportingClientStandards(false);
        }
    };

    const handleDelete = async (standardName: string) => {
        const dynamicDataId = dynamicDataIdByName.get(standardName);
        if (!dynamicDataId) return;
        if (!window.confirm(`Delete the "${standardName}" standard? This cannot be undone.`)) return;
        await deleteDynamicData({ variables: { projectId, ids: [dynamicDataId] } });
        await refetch();
    };

    const handleAdd = async (std: StandardDefinition) => {
        if (!canEdit || !canAccessStandard(std)) return;
        setAddMenuOpen(false);
        if (std.id === CONTENT_DATA_MAPPING_ID) {
            setNewMappingName('');
            setMappingNameError('');
            setAddMappingDialogOpen(true);
            return;
        }
        if (std.mode === 'editor') {
            // Editor creates its own DynamicData entry on first save
            setSelectedId(std.id);
            return;
        }
        // Viewer-mode: create a marker entry so the card appears
        await setDynamicData({
            variables: { projectId, data: { name: std.id, type: 'Standards' } },
        });
        await refetch();
        setSelectedId(std.id);
    };

    const handleCreateMapping = async () => {
        const name = newMappingName.trim();
        if (!name) {
            setMappingNameError('Enter a name for this data mapping.');
            return;
        }
        const slug = getContentDataMappingSlug(name);
        if (contentDataMappings.some(entry => entry.name?.toLocaleLowerCase() === name.toLocaleLowerCase() || entry.slug === slug)) {
            setMappingNameError('A data mapping with this name already exists.');
            return;
        }
        setMappingNameError('');
        await setDynamicData({
            variables: {
                projectId,
                data: {
                    slug,
                    name,
                    type: STANDARD_TYPE,
                    description: 'Maps data values to library content by category',
                },
            },
        });
        const refreshed = await refetch();
        const created = ((refreshed.data?.Project?.dynamicData ?? []) as ProjectStandardEntry[])
            .find(entry => entry.type === STANDARD_TYPE && entry.slug === slug && entry.name === name);
        setAddMappingDialogOpen(false);
        if (created?.id) setSelectedId(created.id);
    };

    /* â”€â”€ Full-editor view â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */
    const selectedMapping = contentDataMappings.find(entry => entry.id === selectedId);
    if (selectedMapping?.id) {
        return (
            <KeyScheduleMapping
                projectId={projectId}
                libraryId={libraryId}
                mappingId={selectedMapping.id}
                mappingName={getContentDataMappingName(selectedMapping)}
                canEdit={canEdit}
                style={style}
                onBack={() => { setSelectedId(null); refetch(); }}
            />
        );
    }

    if (selectedId === 'Nomenclature') {
        return (
            <NomenclatureStandard
                projectId={projectId}
                libraryId={libraryId}
                style={style}
                onBack={() => { setSelectedId(null); refetch(); }}
            />
        );
    }

    if (selectedId === 'Classification') {
        return (
            <ClassificationStandard
                projectId={projectId}
                style={style}
                onBack={() => { setSelectedId(null); refetch(); }}
            />
        );
    }

    if (selectedId === 'Parameter Mapping') {
        return (
            <ParameterMappingStandard
                projectId={projectId}
                style={style}
                onBack={() => { setSelectedId(null); refetch(); }}
            />
        );
    }

    if (selectedId === 'Text Style') {
        return (
            <TextStyleStandard
                projectId={projectId}
                style={style}
                onBack={() => { setSelectedId(null); refetch(); }}
            />
        );
    }

    if (selectedId === 'Must Include Views') {
        return (
            <MustIncludeViewsStandard
                projectId={projectId}
                canEdit={canEdit}
                style={style}
                onBack={() => { setSelectedId(null); refetch(); }}
            />
        );
    }

    if (selectedId) {
        const std = STANDARD_DEFINITIONS.find(s => s.id === selectedId);
        const Icon = std?.icon ?? Construction;
        return (
            <div className="flex flex-col h-full" style={style}>
                <div
                    className="flex items-center gap-3 px-4 py-2.5 border-b flex-shrink-0"
                    style={{ borderColor: 'var(--border-default)' }}
                >
                    <button
                        className="flex items-center gap-1 text-sm font-medium px-2 py-1 rounded-md transition-colors"
                        style={{ color: 'var(--accent-text)', background: 'none', border: 'none', cursor: 'pointer' }}
                        onClick={() => setSelectedId(null)}
                    >
                        <ArrowLeft size={15} /> Back
                    </button>
                    <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                        {std?.title ?? selectedId}
                    </span>
                </div>
                <div className="flex flex-col items-center justify-center flex-1 gap-4">
                    <div
                        className="w-14 h-14 rounded-2xl flex items-center justify-center"
                        style={{ backgroundColor: std?.iconBg }}
                    >
                        <Icon size={28} style={{ color: std?.iconColor }} />
                    </div>
                    <div className="text-center">
                        <p className="text-base font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>
                            {std?.title ?? selectedId}
                        </p>
                        <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>
                            Full editor coming soon
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    /* â”€â”€ Card grid â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */
    return (
        <div className="flex flex-col h-full" style={style} data-testid="Standards">
            {/* Toolbar */}
            <div className="flex items-center justify-end gap-2 px-6 pt-5 pb-2 flex-shrink-0">
                {canUsePreviewFeatures && (
                    <>
                        <button
                            className="btn btn-ghost flex items-center gap-2 text-sm"
                            disabled={!clientDefaultLibraryId || projectStandardEntries.length === 0}
                            onClick={openSaveDialog}
                            title={!clientDefaultLibraryId ? 'The client does not have a Default Library' : undefined}
                        >
                            <Upload size={14} />
                            Save to Client
                            <PreviewBadge />
                        </button>
                        <button
                            className="btn btn-ghost flex items-center gap-2 text-sm"
                            disabled={!clientDefaultLibraryId}
                            onClick={openImportDialog}
                            title={!clientDefaultLibraryId ? 'The client does not have a Default Library' : undefined}
                        >
                            <Download size={14} />
                            Add from Client
                            <PreviewBadge />
                        </button>
                    </>
                )}
                {canEdit && <div className="relative">
                    <button
                        ref={addBtnRef}
                        className="btn-primary flex items-center gap-2 text-sm"
                        disabled={standardsToAdd.length === 0 || adding}
                        onClick={() => setAddMenuOpen(v => !v)}
                    >
                        {adding ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                        Add Standard
                        {canUsePreviewFeatures && <PreviewBadge />}
                    </button>
                    {addMenuOpen && (
                        <div
                            ref={menuRef}
                            className="absolute right-0 top-full mt-1 card z-20 py-1"
                            style={{
                                minWidth: 240,
                                border: '1px solid var(--border-default)',
                                boxShadow: 'var(--shadow-md)',
                            }}
                        >
                            {standardsToAdd.map(std => {
                                const Icon = std.icon;
                                const isAvailable = canAccessStandard(std);
                                return (
                                    <button
                                        key={std.id}
                                        className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors"
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            cursor: isAvailable ? 'pointer' : 'not-allowed',
                                            color: 'var(--text-primary)',
                                            opacity: isAvailable ? 1 : 0.6,
                                        }}
                                        disabled={!isAvailable}
                                        onMouseEnter={e => {
                                            if (isAvailable) e.currentTarget.style.backgroundColor = 'var(--bg-hover)';
                                        }}
                                        onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                                        onClick={() => handleAdd(std)}
                                    >
                                        <div
                                            className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                                            style={{ backgroundColor: std.iconBg }}
                                        >
                                            <Icon size={14} style={{ color: std.iconColor }} />
                                        </div>
                                        <div className="flex-1">
                                            <div className="flex items-center justify-between gap-3">
                                                <p className="font-medium text-sm leading-tight">{std.title}</p>
                                                {std.releaseStatus === 'comingSoon' && (
                                                    <span
                                                        className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full whitespace-nowrap"
                                                        style={{
                                                            color: 'var(--text-secondary)',
                                                            backgroundColor: 'var(--surface-1, var(--surface-100))',
                                                        }}
                                                    >
                                                        {canUsePreviewFeatures ? 'Preview' : 'Coming Soon'}
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-xs leading-tight" style={{ color: 'var(--text-tertiary)' }}>{std.description}</p>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>}
            </div>

            {/* Content */}
            <div className="flex-1 overflow-auto px-6 pt-2 pb-6">
                {workflowStatus && (
                    <div className="mb-3 px-3 py-2 rounded-lg text-sm" style={{ color: 'var(--success, #16a34a)', backgroundColor: 'var(--success-soft, #dcfce7)' }}>
                        {workflowStatus}
                    </div>
                )}
                {loading ? (
                    <div className="flex items-center justify-center h-40">
                        <Loader2 size={20} className="animate-spin" style={{ color: 'var(--text-tertiary)' }} />
                    </div>
                ) : activeStandards.length === 0 && contentDataMappings.length === 0 && unknownStoredNames.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-48 gap-3">
                        <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>No standards configured</p>
                        {canEdit && (
                            <button
                                className="text-sm font-medium"
                                style={{ color: 'var(--accent)', background: 'none', border: 'none', cursor: 'pointer' }}
                                onClick={() => setAddMenuOpen(true)}
                            >
                                + Add your first standard
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {contentDataMappings.map(entry => {
                            const definition = STANDARD_DEFINITIONS.find(std => std.id === CONTENT_DATA_MAPPING_ID)!;
                            const isAvailable = canAccessStandard(definition);
                            const displayName = getContentDataMappingName(entry);
                            return (
                                <div key={entry.id || displayName} className="relative">
                                    <button
                                        onClick={() => { if (isAvailable && entry.id) setSelectedId(entry.id); }}
                                        disabled={!isAvailable}
                                        className="card p-5 text-left transition-all w-full"
                                        style={{
                                            border: '1px solid var(--border-default)',
                                            cursor: isAvailable ? 'pointer' : 'not-allowed',
                                            background: 'var(--bg-card)',
                                            opacity: isAvailable ? 1 : 0.65,
                                        }}
                                    >
                                        <div className="flex items-start justify-between mb-3">
                                            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: definition.iconBg }}>
                                                <TableProperties size={20} style={{ color: definition.iconColor }} />
                                            </div>
                                            {!canEdit && <ChevronRight size={18} style={{ color: 'var(--text-tertiary)' }} />}
                                        </div>
                                        <h3 className="text-base font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>{displayName}</h3>
                                        <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>Content Data Mapping</p>
                                    </button>
                                    {canEdit && isAvailable && entry.id && (
                                        <button
                                            onClick={() => handleDelete(displayName)}
                                            className="absolute top-2.5 right-2.5 p-1.5 rounded-md transition-colors"
                                            style={{ color: 'var(--text-tertiary)', background: 'none', border: 'none', cursor: 'pointer' }}
                                            title={`Delete ${displayName}`}
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    )}
                                </div>
                            );
                        })}
                        {activeStandards.map(std => {
                            const Icon = std.icon;
                            const isAvailable = canAccessStandard(std);
                            return (
                                <div key={std.id} className="relative">
                                    <button
                                        onClick={() => {
                                            if (isAvailable) setSelectedId(std.id);
                                        }}
                                        disabled={!isAvailable}
                                        className="card p-5 text-left transition-all w-full"
                                        style={{
                                            border: '1px solid var(--border-default)',
                                            cursor: isAvailable ? 'pointer' : 'not-allowed',
                                            background: 'var(--bg-card)',
                                            opacity: isAvailable ? 1 : 0.65,
                                        }}
                                        onMouseOver={e => {
                                            if (!isAvailable) return;
                                            e.currentTarget.style.borderColor = 'var(--accent)';
                                            e.currentTarget.style.transform = 'translateY(-2px)';
                                        }}
                                        onMouseOut={e => { e.currentTarget.style.borderColor = 'var(--border-default)'; e.currentTarget.style.transform = 'translateY(0)'; }}
                                    >
                                        <div className="flex items-start justify-between mb-3">
                                            <div
                                                className="w-10 h-10 rounded-xl flex items-center justify-center"
                                                style={{ backgroundColor: std.iconBg }}
                                            >
                                                <Icon size={20} style={{ color: std.iconColor }} />
                                            </div>
                                            {std.releaseStatus === 'comingSoon' && !canUsePreviewFeatures ? (
                                                <span
                                                    className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full"
                                                    style={{
                                                        color: 'var(--text-secondary)',
                                                        backgroundColor: 'var(--surface-1, var(--surface-100))',
                                                    }}
                                                >
                                                    Coming Soon
                                                </span>
                                            ) : (
                                                !canEdit && <ChevronRight size={18} style={{ color: 'var(--text-tertiary)' }} />
                                            )}
                                        </div>
                                        <h3 className="text-base font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>
                                            {std.title}
                                        </h3>
                                        <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                                            {std.description}
                                        </p>
                                    </button>
                                    {canEdit && isAvailable && (
                                        <button
                                            onClick={() => handleDelete(std.id)}
                                            className="absolute top-2.5 right-2.5 p-1.5 rounded-md transition-colors"
                                            style={{ color: 'var(--text-tertiary)', background: 'none', border: 'none', cursor: 'pointer' }}
                                            onMouseEnter={e => { e.currentTarget.style.color = 'var(--danger, #dc2626)'; e.currentTarget.style.backgroundColor = 'var(--danger-soft, #fee2e2)'; }}
                                            onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-tertiary)'; e.currentTarget.style.backgroundColor = 'transparent'; }}
                                            title={`Delete ${std.title}`}
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    )}
                                </div>
                            );
                        })}
                        {/* Unknown / server-defined standards — view-only generic cards */}
                        {unknownStoredNames.map(name => (
                            <div key={name} className="relative">
                                <button
                                    onClick={() => setSelectedId(name)}
                                    className="card p-5 text-left transition-all w-full"
                                    style={{ border: '1px solid var(--border-default)', cursor: 'pointer', background: 'var(--bg-card)' }}
                                    onMouseOver={e => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                                    onMouseOut={e => { e.currentTarget.style.borderColor = 'var(--border-default)'; e.currentTarget.style.transform = 'translateY(0)'; }}
                                >
                                    <div className="flex items-start justify-between mb-3">
                                        <div
                                            className="w-10 h-10 rounded-xl flex items-center justify-center"
                                            style={{ backgroundColor: 'var(--surface-1, var(--surface-100))' }}
                                        >
                                            <Construction size={20} style={{ color: 'var(--text-secondary)' }} />
                                        </div>
                                        {!canEdit && <ChevronRight size={18} style={{ color: 'var(--text-tertiary)' }} />}
                                    </div>
                                    <h3 className="text-base font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>
                                        {name}
                                    </h3>
                                </button>
                                {canEdit && (
                                    <button
                                        onClick={() => handleDelete(name)}
                                        className="absolute top-2.5 right-2.5 p-1.5 rounded-md transition-colors"
                                        style={{ color: 'var(--text-tertiary)', background: 'none', border: 'none', cursor: 'pointer' }}
                                        onMouseEnter={e => { e.currentTarget.style.color = 'var(--danger, #dc2626)'; e.currentTarget.style.backgroundColor = 'var(--danger-soft, #fee2e2)'; }}
                                        onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-tertiary)'; e.currentTarget.style.backgroundColor = 'transparent'; }}
                                        title={`Delete ${name}`}
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <DialogBase
                title="Add Content Data Mapping"
                type="Custom"
                visible={addMappingDialogOpen}
                onClose={() => setAddMappingDialogOpen(false)}
                validationError={mappingNameError}
                isLoading={adding}
                actions={(
                    <>
                        <button className="btn btn-secondary" onClick={() => setAddMappingDialogOpen(false)}>Cancel</button>
                        <button className="btn btn-primary" onClick={handleCreateMapping}>Add mapping</button>
                    </>
                )}
            >
                <label className="block text-sm font-medium mb-2" htmlFor="content-data-mapping-name">Mapping name</label>
                <input
                    id="content-data-mapping-name"
                    className="input-base"
                    value={newMappingName}
                    onChange={event => setNewMappingName(event.target.value)}
                    onKeyDown={event => { if (event.key === 'Enter') handleCreateMapping(); }}
                    placeholder="e.g. Door hardware mapping"
                    autoFocus
                />
            </DialogBase>

            <DialogBase
                title="Save Standards to Client"
                type="Custom"
                visible={saveDialogOpen}
                onClose={() => setSaveDialogOpen(false)}
                validationError={workflowError}
                isLoading={isSavingClientStandards}
                actions={(
                    <>
                        <button className="btn btn-secondary" onClick={() => setSaveDialogOpen(false)}>Cancel</button>
                        <button className="btn btn-primary" onClick={handleSaveToClient}>Save new version</button>
                    </>
                )}
            >
                <p className="text-sm mb-4">
                    Save every configured project standard to <strong>{clientName || 'the client'}</strong>. This creates or updates <strong>{CLIENT_STANDARDS_NAME}</strong> in the client Default Library.
                </p>
                <div className="space-y-2">
                    {projectStandardEntries.map(entry => (
                        <div key={entry.id || entry.name} className="flex items-center justify-between px-3 py-2 rounded-lg" style={{ backgroundColor: 'var(--surface-1)' }}>
                            <span className="text-sm font-medium">{isContentDataMapping(entry) ? getContentDataMappingName(entry) : entry.name}</span>
                            <span className="text-xs" style={{ color: entry.data ? 'var(--success, #16a34a)' : 'var(--danger)' }}>
                                {entry.data ? 'Configured' : 'No saved payload'}
                            </span>
                        </div>
                    ))}
                </div>
            </DialogBase>

            <DialogBase
                title="Add Client Standards to Project"
                type="Custom"
                visible={importDialogOpen}
                onClose={() => setImportDialogOpen(false)}
                validationError={workflowError}
                isLoading={clientStandardsResult.loading || isImportingClientStandards}
                actions={(
                    <>
                        <button className="btn btn-secondary" onClick={() => setImportDialogOpen(false)}>Cancel</button>
                        <button className="btn btn-primary" disabled={selectedImportNames.size === 0} onClick={handleImportFromClient}>Add selected</button>
                    </>
                )}
            >
                {!clientStandardContent && !clientStandardsResult.loading ? (
                    <p className="text-sm">No {CLIENT_STANDARDS_NAME} content was found in the client Default Library.</p>
                ) : (
                    <>
                        <p className="text-sm mb-4">Selected standards are copied into this project and can then be tailored independently. Existing project standards are kept unless you explicitly select them.</p>
                        <div className="space-y-2">
                            {clientStandardTypes.map(type => {
                                const name = type.name as string;
                                const slug = getContentDataMappingEnvelope(type)?.slug;
                                const exists = dynamicDataIdByName.has(name)
                                    || !!(slug && dynamicDataIdBySlug.has(slug));
                                return (
                                    <label key={type.id || name} className="flex items-start gap-3 px-3 py-2 rounded-lg cursor-pointer" style={{ backgroundColor: 'var(--surface-1)' }}>
                                        <input type="checkbox" checked={selectedImportNames.has(name)} onChange={() => toggleImportName(name)} />
                                        <span className="flex-1">
                                            <span className="block text-sm font-medium">{name}</span>
                                            {type.description && <span className="block text-xs" style={{ color: 'var(--text-tertiary)' }}>{type.description}</span>}
                                        </span>
                                        {exists && <span className="text-xs" style={{ color: 'var(--warning)' }}>Replaces project value</span>}
                                    </label>
                                );
                            })}
                        </div>
                    </>
                )}
            </DialogBase>
        </div>
    );
};

export default Standards;
